// Factory functions and limits for design elements.
// Дефолты — с ref `createSubBlock` (oa-studio-prototype/src/types/designBlock.ts), кроме
// текстов: они ПУСТЫЕ, а «Заголовок»/«Ячейка» холст рисует серой подсказкой. Текст-заготовка
// была настоящим значением — курс публиковался с «Текст абзаца» и «Новый» внутри.
// Лимиты — рефовские; часть из них строже бековских (см. спек §3), но после
// синка 2026-07-27 HEADING/TEXT_ROTATION/IMAGE_ROTATION совпадают с бековскими
// ТОЧНО (backend больше не отклонит значение вплоть до этого числа).
import type {
  DesignBadgeItem,
  DesignElement,
  DesignElementBase,
  DesignElementType,
  DesignTableCell,
} from '@/types/blocks'

/** Студийный потолок текста дизайн-блока. Бэк меряет 5000 (`DESIGN_LIMITS.TEXT`) по `len()` после
 *  nh3, студия — `.length` после DOMPurify; сериализаторы расходятся на сущностях, точное
 *  совпадение недостижимо, поэтому запас 50. Счётчик, запрет ввода/вставки и проверка
 *  перед сохранением идут по этой константе, а не по серверной. */
export const DESIGN_TEXT_STUDIO_LIMIT = 4950

export const DESIGN_LIMITS = {
  ELEMENTS: 20,
  HEADING: 38, // = бековский лимит (синк 2026-07-27), не "строже"
  TEXT: 5000,
  TEXT_ROTATION: 2, // = бековский лимит (синк 2026-07-27), не "строже"
  IMAGE_ROTATION: 4, // = бековский лимит (синк 2026-07-27), не "строже"
  BADGES: 9,
  BADGE_TEXT: 20,
  TABLE_ROWS_MIN: 2,
  TABLE_ROWS_MAX: 5,
  TABLE_COLS_MIN: 2,
  TABLE_COLS_MAX: 4,
  // Ничего не валидирует: свою иконку задать нельзя (вкладка «Своя» — заглушка
  // «скоро»), а имя lucide в 2048 символов не упрётся. Запись контракта бека на
  // случай, когда кастомный путь (легаси-данные это поле ещё носят) оживёт.
  ICON_VALUE: 2048,
} as const

/** id элемента/бейджа/ячейки: ≤64 симв., уникальность на нас — бек её не проверяет. */
export const newDesignId = (): string => crypto.randomUUID()

const baseFields = (): Omit<DesignElementBase, 'id'> => ({
  textAlign: 'center',
  textSize: 'medium',
  fontWeight: 'normal',
  padding: 'medium',
  backdrop: 'none',
  backdropRounded: true,
  highlight: 'none',
})

/** Новый бейдж списка. Перевод ВСЕГДА пустой: подставленный текст уехал бы в оба
 *  языковых листа. Единственная фабрика бейджа — форма, канвас-заглушка и дефолт
 *  элемента обязаны звать её, иначе поля разъедутся по трём литералам. */
export const newBadgeItem = (text: string): DesignBadgeItem => ({
  id: newDesignId(),
  text,
  textEn: '',
  iconType: 'none',
  iconValue: null,
})

/** Новая ячейка таблицы (см. `newBadgeItem` про перевод). Пустая по умолчанию:
 *  ±столбец/строка добивают матрицу до прямоугольника именно такими. */
export const newTableCell = (content = ''): DesignTableCell => ({
  id: newDesignId(),
  content,
  contentEn: '',
})

const buildDesignElement = (type: DesignElementType): DesignElement => {
  const base = { id: newDesignId(), ...baseFields() }
  switch (type) {
    case 'heading':
      return {
        ...base,
        type,
        content: '',
        contentEn: '',
        textSize: 'large',
        fontWeight: 'bold',
      }
    case 'text':
      // Абзац по умолчанию слева (заголовок — по центру): так читается длинный текст.
      return {
        ...base,
        type,
        content: '',
        contentEn: '',
        textAlign: 'left',
        textRotation: 0,
      }
    case 'image':
      return { ...base, type, imageUrl: '', imageUrlEn: '', imageRotation: 0 }
    case 'badge':
      return {
        ...base,
        type,
        badges: [newBadgeItem('')],
        badgeVariant: 'pastel',
        badgeSize: 'xlarge',
        badgeLayout: 'horizontal',
      }
    case 'table':
      return {
        ...base,
        type,
        textAlign: 'left',
        tableData: [
          [newTableCell(), newTableCell()],
          [newTableCell(), newTableCell()],
        ],
        tableStyle: 'striped',
        tableTextSize: 'medium',
      }
  }
}

/** Элемент КОНКРЕТНОГО типа: `createDesignElement('table')` — это `DesignTable`,
 *  а не весь юнион. Раньше вызывающие снимали юнион кастом (`as DesignBadge` в
 *  11 местах), и `createDesignElement('table') as DesignHeading` тоже компилировался.
 *  Каст остался ровно один — здесь, на возврате. */
export const createDesignElement = <T extends DesignElementType>(
  type: T,
): Extract<DesignElement, { type: T }> =>
  buildDesignElement(type) as Extract<DesignElement, { type: T }>
